/**
 * RAM — AddressChange action.
 *
 * Calls `apiClient.accounts.custMod` with the new address fields. AuthMethod
 * is required (InPerson / Phone / Mail / Online). Default InPerson.
 *
 * 2026-04-22 update: per RAM audit G-1, City/State/Zip for the PHYSICAL
 * address + USPS DPV verification were missing. Now ported from the RAO
 * PersonModal pattern (useUsps hook). Ports preserve CustMod field names
 * expected by Core Director. A USPS correction is shown line by line and
 * applied only when the officer clicks "Accept Standardized" (2026-10-16).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useUsps, DPV_LABELS } from '@/hooks/useUsps';
import { asIso8601 } from '@retail-ops/shared-types';
import type { CustModRequest, SessionEventPayload } from '@retail-ops/shared-types';
import type { RamTarget } from '../types';
import { uspsZip } from '@/utils/zipPlus4';
import { UspsSuggestionPanel } from '@/components/address/UspsSuggestionPanel';

export interface AddressChangeProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

type AuthMethod = CustModRequest['AuthMethod'];

// US state codes (short list — matches the RAO PersonModal set).
const US_STATES: ReadonlyArray<string> = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

export default function AddressChange({
  target,
  onDone,
  onCancel,
}: AddressChangeProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const usps = useUsps();

  // Physical address.
  const [streetAddr, setStreetAddr] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [stateCode, setStateCode] = useState<string>('');
  const [zip, setZip] = useState<string>('');

  // Mailing address (optional — only when different from physical).
  const [useMailing, setUseMailing] = useState<boolean>(false);
  const [mailAddr, setMailAddr] = useState<string>('');
  const [mailCity, setMailCity] = useState<string>('');
  const [mailState, setMailState] = useState<string>('');
  const [mailZip, setMailZip] = useState<string>('');

  const [authMethod, setAuthMethod] = useState<AuthMethod>('InPerson');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<boolean>(false);

  const canSave =
    !saving &&
    streetAddr.trim().length > 0 &&
    stateCode.length === 2 &&
    zip.replace(/\D/g, '').length >= 5;

  // ZIP → city/state autofill on 5-digit entry (same UX as RAO).
  const lastZipRef = useRef('');
  const onZipChange = useCallback(
    (v: string) => {
      setZip(v);
      const clean = v.replace(/\D/g, '').slice(0, 5);
      if (clean.length === 5 && clean !== lastZipRef.current) {
        lastZipRef.current = clean;
        void usps.autoFillFromZip(clean).then((r) => {
          if (!r) return;
          if (!city) setCity(r.city);
          if (!stateCode) setStateCode(r.state);
        });
      }
    },
    [usps, city, stateCode]
  );

  // Reset DPV badge when any physical field changes.
  useEffect(() => {
    usps.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streetAddr, city, stateCode, zip]);

  const verify = useCallback(async () => {
    if (!streetAddr || !stateCode) return;
    // Show-then-accept (Collette Cruse life-ring report 2026-10-16; Bill approved
    // 2026-10-16): Verify only asks USPS. The suggestion is shown line by line
    // below and is applied only by "Accept Standardized".
    await usps.validateAddress({
      streetAddress: streetAddr,
      state: stateCode,
      city: city || undefined,
      ZIPCode: zip || undefined,
    });
  }, [usps, streetAddr, stateCode, city, zip]);

  // The USPS suggestion as it would be saved (unit kept, ZIP+4 policy BACKLOG-1292).
  const suggestion = useMemo(() => {
    const a = usps.result?.address as
      | { streetAddress?: string; secondaryAddress?: string; city?: string; state?: string; ZIPCode?: string; ZIPPlus4?: string }
      | undefined;
    if (!a || !a.streetAddress) return null;
    const unit = (a.secondaryAddress ?? '').trim();
    const street = unit && !a.streetAddress.toUpperCase().includes(unit.toUpperCase())
      ? `${a.streetAddress} ${unit}`
      : a.streetAddress;
    return {
      street,
      city: a.city ?? '',
      state: a.state ?? '',
      zip: uspsZip(a.ZIPCode, a.ZIPPlus4),
    };
  }, [usps.result]);

  const acceptSuggestion = useCallback((): void => {
    if (!suggestion) return;
    if (suggestion.street && suggestion.street !== streetAddr) setStreetAddr(suggestion.street);
    if (suggestion.city && suggestion.city !== city) setCity(suggestion.city);
    if (suggestion.state && suggestion.state !== stateCode) setStateCode(suggestion.state);
    if (suggestion.zip && suggestion.zip !== zip) setZip(suggestion.zip);
  }, [suggestion, streetAddr, city, stateCode, zip]);

  const dpv = usps.result?.additionalInfo?.DPVConfirmation ?? '';
  const dpvInfo = dpv ? DPV_LABELS[dpv] : null;

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const req: CustModRequest = {
        CifId: target.cifId,
        AuthMethod: authMethod,
        OffCode: '000',
        StreetAddr1: streetAddr,
        City: city || undefined,
        State: stateCode || undefined,
        Zip: zip || undefined,
        ...(useMailing
          ? {
              MailAddr: mailAddr || undefined,
              MailCity: mailCity || undefined,
              MailState: mailState || undefined,
              MailZip: mailZip || undefined,
            }
          : {}),
      };
      const res = await client.accounts.custMod(req);

      const payload: SessionEventPayload = {
        eventType: 'cif-modified',
        eventCategory: 'cif-account',
        severity: 'info',
        actor: user?.upn ?? 'fsr',
        timestamp: asIso8601(new Date().toISOString()),
        payloadJson: JSON.stringify({
          action: 'address-change',
          cifId: target.cifId,
          authMethod,
          dpv: dpv || 'unverified',
        }),
      };
      client.retail.events.write(payload).catch(() => undefined);

      if (!res.success) {
        setError(res.error ?? 'CustMod returned a non-success result.');
        setSaving(false);
        return;
      }
      setDone(true);
      setSaving(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to save address.');
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">Address updated for CIF {target.cifId}.</Banner>
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Address change</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
      </div>

      <Banner tone="info">
        Signed change-of-contact form required for material address changes —
        capture in RSC before submit.
      </Banner>

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          Auth method
          <select
            className="ram-input"
            value={authMethod}
            onChange={(e) => setAuthMethod(e.target.value as AuthMethod)}
          >
            <option value="InPerson">In person</option>
            <option value="Phone">Phone</option>
            <option value="Mail">Mail</option>
            <option value="Online">Online</option>
          </select>
        </label>

        <h3 className="ram-wizard__section-title">Physical address</h3>
        <label className="ram-search__label">
          Street address
          <input
            className="ram-input"
            value={streetAddr}
            onChange={(e) => setStreetAddr(e.target.value)}
            maxLength={60}
          />
        </label>
        <div className="ram-wizard__row">
          <label className="ram-search__label" style={{ flex: 2 }}>
            City
            <input
              className="ram-input"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              maxLength={30}
            />
          </label>
          <label className="ram-search__label" style={{ flex: 1 }}>
            State
            <select
              className="ram-input"
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              title="State"
            >
              <option value="">--</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label className="ram-search__label" style={{ flex: 1 }}>
            ZIP
            <input
              className="ram-input"
              value={zip}
              onChange={(e) => onZipChange(e.target.value)}
              maxLength={10}
              inputMode="numeric"
              placeholder="00000 or 00000-0000"
            />
          </label>
        </div>
        <div className="ram-wizard__row" style={{ alignItems: 'center', gap: 12 }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { void verify(); }}
            disabled={usps.verifying || !streetAddr || !stateCode}
          >
            {usps.verifying ? 'Verifying…' : 'Verify with USPS'}
          </Button>
          {dpvInfo && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 8px',
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 600,
                background: dpvInfo.bg,
                color: dpvInfo.color,
              }}
            >
              {dpv === 'Y' ? '✓' : dpv === 'N' ? '✗' : '⚠'} {dpvInfo.label}
            </span>
          )}
          {usps.error && (
            <span style={{ fontSize: 12, color: 'var(--pcb-red)' }}>{usps.error}</span>
          )}
        </div>
        {suggestion && (
          <UspsSuggestionPanel
            current={{ street: streetAddr, city, state: stateCode, zip }}
            suggested={suggestion}
            onAccept={acceptSuggestion}
          />
        )}

        <label className="ram-search__label" style={{ marginTop: 8 }}>
          <input
            type="checkbox"
            checked={useMailing}
            onChange={(e) => setUseMailing(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          Mailing address is different from physical address
        </label>

        {useMailing && (
          <>
            <h3 className="ram-wizard__section-title">Mailing address</h3>
            <label className="ram-search__label">
              Mail address
              <input
                className="ram-input"
                value={mailAddr}
                onChange={(e) => setMailAddr(e.target.value)}
                maxLength={60}
              />
            </label>
            <div className="ram-wizard__row">
              <label className="ram-search__label" style={{ flex: 2 }}>
                Mail city
                <input
                  className="ram-input"
                  value={mailCity}
                  onChange={(e) => setMailCity(e.target.value)}
                  maxLength={30}
                />
              </label>
              <label className="ram-search__label" style={{ flex: 1 }}>
                Mail state
                <select
                  className="ram-input"
                  value={mailState}
                  onChange={(e) => setMailState(e.target.value)}
                  title="Mail state"
                >
                  <option value="">--</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="ram-search__label" style={{ flex: 1 }}>
                Mail ZIP
                <input
                  className="ram-input"
                  value={mailZip}
                  onChange={(e) => setMailZip(e.target.value)}
                  maxLength={10}
                  inputMode="numeric"
                />
              </label>
            </div>
          </>
        )}
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Saving…' : 'Submit address change'}
        </Button>
      </div>
    </div>
  );
}
